import { viewingOtherUser } from './appUserContext.js';
import { pushAppState } from './appNavigation.js';
import { qS } from '../_default.js';
import { activity } from '../activity.js';
import { confirm } from '../dialog.js';
import { notification } from '../notification.js';
import { tooltip } from '../tooltip.js';
import { appCleanUI } from './appCleanUI.js';
import {
  disconnectPlugin,
  getPlugins,
  updatePluginSettings,
} from './appPlugin.js';
import { renderPluginsChatControl } from './appPluginControl.js';
import {
  ensurePluginFlow,
  renderPluginFlow,
} from './appPluginAddRender.js';
import { renderSettingsSubnav } from './appSettingsSubnav.js';
import { t } from './appTranslation.js';
import { getPluginActivityMetadata } from './pluginActivity.mjs';

const ensurePluginsPageContainer = function(){
  let container = qS('#app_plugin_container');
  if(container){
    return container;
  }

  container = document.createElement('div');
  container.classList.add('app-container', 'app-page-container', 'app-plugin-container', 'hidden');
  container.id = 'app_plugin_container';
  container.innerHTML = `
    <div class="app-container-inner app-container-inner-no-top-bar">
      <div class="scrollable" id="app_plugin_scroll">
        <div class="app-settings-list-inner app-plugin-list-inner inner" id="app_plugin_list_inner">
          <div class="plugin-section app-account-section">
            <div class="list-title-sub">${t('plugins.pluginsTitle')}</div>
            <div class="plugin-list" id="plugin_list"></div>
          </div>
          <div class="app-account-section plugin-flow-wrapper" id="plugin_flow_wrapper"></div>
        </div>
      </div>
    </div>
  `;
  qS('#app_content')?.appendChild(container);
  ensurePluginFlow();
  return container;
};

const setButtonLoading = function(button, loading){
  if(!button){ return; }
  button.disabled = Boolean(loading);
  button.classList.toggle('button-loading', Boolean(loading));
};

const getPluginTypeId = function(pluginItem){
  if(pluginItem?.custom || pluginItem?.icon === 'custom'){
    return 'custom';
  }
  return String(pluginItem?.icon || 'custom').replace(/[^a-z0-9-]/gi, '').toLowerCase() || 'custom';
};

const renderPluginCard = function(pluginItem){
  const connected = pluginItem.connection?.status === 'connected';
  const readOnly = viewingOtherUser();
  const pluginTypeId = getPluginTypeId(pluginItem);
  const card = document.createElement('div');
  card.classList.add('plugin-card');
  card.dataset.pluginId = pluginTypeId;
  if(connected){ card.classList.add('connected'); }

  const icon = document.createElement('div');
  icon.classList.add('plugin-icon', `plugin-icon-${pluginTypeId}`);
  if(pluginItem.toolkitSlug){
    icon.style.backgroundImage = `url("https://logos.composio.dev/api/${encodeURIComponent(pluginItem.toolkitSlug)}")`;
  }
  icon.setAttribute('aria-hidden', 'true');
  card.appendChild(icon);

  const body = document.createElement('div');
  body.classList.add('plugin-body');
  card.appendChild(body);

  const titleRow = document.createElement('div');
  titleRow.classList.add('plugin-title-row');
  body.appendChild(titleRow);

  const title = document.createElement(connected ? 'input' : 'div');
  title.classList.add('plugin-title');
  if(connected){
    title.type = 'text';
    title.readOnly = readOnly;
    title.value = pluginItem.connection?.name || pluginItem.name;
    title.maxLength = 80;
    title.setAttribute('aria-label', t('plugins.connectionName'));
    const saveName = async function(){
      if(readOnly){ return; }
      const nextName = title.value.replace(/\s+/g, ' ').trim();
      if(!nextName || nextName === (pluginItem.connection?.name || pluginItem.name)){
        title.value = pluginItem.connection?.name || pluginItem.name;
        return;
      }
      const loadingNotification = notification.showLoading(t('common.saving'));
      title.disabled = true;
      try {
        await updatePluginSettings(pluginItem.connection.connectionId, { name: nextName });
        pluginItem.connection.name = nextName;
        renderPluginsChatControl();
        notification.update(t('notifications.saved'), loadingNotification, 'success');
      } catch (error) {
        title.value = pluginItem.connection?.name || pluginItem.name;
        notification.update(
          error.message || t('plugins.updateFailed'),
          loadingNotification,
          'error',
          true
        );
      }
      title.disabled = false;
    };
    title.addEventListener('change', saveName);
    title.addEventListener('keydown', (event) => {
      if(event.key === 'Enter'){
        event.preventDefault();
        title.blur();
      }
    });
  }else{
    title.textContent = pluginItem.name;
  }
  titleRow.appendChild(title);

  if(connected){
    const controls = document.createElement('div');
    controls.classList.add('plugin-controls');
    card.appendChild(controls);

    const writeEnabled = pluginItem.connection?.allowWriteActions === true;
    const accessLabel = t(writeEnabled
      ? 'plugins.readWriteStatus'
      : 'plugins.readOnlyStatus');
    const accessIndicator = document.createElement('button');
    accessIndicator.type = 'button';
    accessIndicator.classList.add(
      'plugin-access-indicator',
      writeEnabled ? 'read-write' : 'read-only'
    );
    accessIndicator.setAttribute('aria-label', accessLabel);
    accessIndicator.dataset.tooltip = accessLabel;
    accessIndicator.dataset.tooltipClick = 'no-hover';
    controls.appendChild(accessIndicator);

    const disconnectButton = document.createElement('button');
    disconnectButton.type = 'button';
    disconnectButton.disabled = readOnly;
    disconnectButton.classList.add('button-delete');
    disconnectButton.setAttribute('aria-label', t('plugins.disconnect'));
    disconnectButton.dataset.tooltip = t('plugins.disconnect');
    disconnectButton.dataset.tooltipClick = 'no-hover';
    controls.appendChild(disconnectButton);
    disconnectButton.addEventListener('click', async () => {
      if(readOnly){ return; }
      confirm(t('plugins.disconnectConfirm', { pluginName: pluginItem.name }), async () => {
        setButtonLoading(disconnectButton, true);
        const list = card.parentElement;
        const nextCard = card.nextSibling;
        const loadingNotification = notification.showLoading(t('plugins.deleting'));
        let emptyState = null;
        card.remove();
        if(list && !list.querySelector('.plugin-card')){
          emptyState = document.createElement('div');
          emptyState.classList.add('plugin-empty', 'list-text-sub');
          emptyState.textContent = t('plugins.noPlugins');
          list.appendChild(emptyState);
        }
        try {
          await disconnectPlugin(pluginItem.connection.connectionId);
          renderPluginsChatControl();
          notification.update(t('notifications.deleted'), loadingNotification, 'success');
          activity.log(
            activity.activityTypes.pluginDelete,
            getPluginActivityMetadata(pluginItem)
          );
        } catch (error) {
          emptyState?.remove();
          if(list){
            list.insertBefore(card, nextCard?.parentElement === list ? nextCard : null);
          }
          notification.update(
            error.message || t('plugins.disconnectFailed'),
            loadingNotification,
            'error',
            true
          );
          setButtonLoading(disconnectButton, false);
        }
      });
    });
  }

  return card;
};

const renderPluginList = async function(){
  const list = qS('#plugin_list');
  if(!list){ return; }
  list.innerHTML = `<div class="plugin-empty list-text-sub">${t('common.loading')}</div>`;

  try {
    const pluginList = await getPlugins({ force: true });
    list.innerHTML = '';
    let connectionCount = 0;
    pluginList.forEach(pluginItem => {
      const connectionList = Array.isArray(pluginItem.connectionList) ? pluginItem.connectionList : [];
      connectionList.forEach(connection => {
        connectionCount += 1;
        list.appendChild(renderPluginCard({ ...pluginItem, connection }));
      });
    });
    if(connectionCount === 0){
      const emptyState = document.createElement('div');
      emptyState.classList.add('plugin-empty', 'list-text-sub');
      emptyState.textContent = t('plugins.noPlugins');
      list.appendChild(emptyState);
    }
    requestAnimationFrame(() => tooltip.addNewEventHandlers());
  } catch (error) {
    list.innerHTML = '';
    const errorState = document.createElement('div');
    errorState.classList.add('plugin-empty', 'list-text-sub');
    errorState.textContent = error.message || t('plugins.loadFailed');
    list.appendChild(errorState);
  }
};

const appPluginRender = function(goBack = false){
  appCleanUI();
  const container = ensurePluginsPageContainer();
  qS('#app_sidebar_add_icon').classList.remove('inactive');
  container.classList.remove('hidden');
  renderSettingsSubnav('/app/plugins/', '#app_plugin_container');
  renderPluginList();
  renderPluginFlow();

  if(!goBack){
    pushAppState({}, '', '/app/plugins/');
  }
};

export { appPluginRender, ensurePluginsPageContainer, renderPluginCard, renderPluginList };
